<template>
  <div class="group relative h-full f-col overflow-hidden rounded-sm" :class="bgClass">
    <base-button
      v-if="quickshop"
      :aria-describedby="product.id"
      :aria-label="$t.add"
      class="absolute right-0 top-0 z-3 p-2"
      data-test-id="product-card-add"
      @click="handleQuickshop($event)"
    >
      <span class="h-6 w-6 flex center rounded-xs bg-white bg-op-75 p-1 shadow-xs backdrop-blur-0.75 duration">
        <vf-icon name="plus" size="xs" />
      </span>
    </base-button>
    <base-picture
      v-bind="size"
      :alt="image.alt"
      class="w-full overflow-hidden"
      :props-img="{ class: 'duration-800 group-hover:scale-105' }"
      :src="getResponsiveImageTransformations(image.src, {
        ...size,
        ...transformations,
      })"
    />
    <div v-style:c="textColor" class="p-3 space-y-3">
      <base-link :id class="line-clamp-4 w-fit body-2" :to @click="handleClick">
        {{ title }}
        <span class="absolute-0" />
      </base-link>
      <product-pricing
        class-wrapper="o:overline-1"
        :currency
        :price
        :show-discount-percentage="$feature.showDiscountPercentageOnCatalog"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { RecommendationStyle } from '#content/contexts/recommendation'
import type { ProductVariants } from '#root/api/clients/product/data-contracts'
import type { ProductCardRecommendedProps } from '#types/components/cms/product-card-recommended'
import type { HistoryState } from 'vue-router'
import { RecommendationStyleContextKey } from '#content/contexts/recommendation'

const {
  currency,
  gtmConfig,
  id,
  image,
  price,
  quickshop = true,
  title,
  url,
  variant,
} = defineProps<ProductCardRecommendedProps>()

const emit = defineEmits<{ 'product-click': [e: PointerEvent] }>()

const route = useRoute()

const searchType = inject('searchType', undefined) as HistoryState | undefined
const suggestedPhrase = inject('suggestedPhrase', undefined) as HistoryState | undefined

const product = computed(() => ({
  id,
  name: title,
  url,
  price,
  currency,
  attributes: [],
  variants: {} as ProductVariants
}))

const closeQuickshopOnAdd = route.name?.toString().includes('cart')

const { size: imageSizeMap, transformations } = useAppConfig().components.cms.productRecommendations.presets

const size = imageSizeMap[variant]

const { DialogQuickshop } = useDialogsStore()

const { textColor, whiteBackground } = inject(RecommendationStyleContextKey, {} as RecommendationStyle)

const bgClass = whiteBackground === false ? '' : 'bg-white'

const handleClick = (e: PointerEvent) => {
  setInteractionOrigin('search', true)
  emit('product-click', e)
}

const to = computed(() => {
  const [path, queryString] = url.split('?')
  const query = queryString ? Object.fromEntries(new URLSearchParams(queryString)) : {}
  const state = searchType ? { category: `Search: ${searchType}`, searchType, suggestedPhrase } : {}

  return { path, query, state }
})

const handleQuickshop = (e: PointerEvent) => {
  emit('product-click', e)
  DialogQuickshop.keep().open(
    { product: product.value, closeOnAdd: closeQuickshopOnAdd, gtmConfig, variant: 'detailed' },
    { size: { sm: 'lg', lg: 'full' } }
  )
}
</script>
